"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { mp3FilenameFromTitle } from "@/lib/integrations/polly/mp3-filename";

export interface SummarySpeechOptions {
  /** Base name for saved/downloaded MP3 (`.mp3` added server-side). */
  filename?: string;
}

/**
 * "Read me my summary" — fetch a Polly MP3 of the given text from
 * `POST /api/ai/tts` and play it. Returns play/stop controls plus loading,
 * playing, and error state for the button to render.
 *
 * The audio is fetched lazily on first play and cached for the lifetime of the
 * component (re-play doesn't re-synthesize unless the text changes). The object
 * URL + audio element are cleaned up on unmount.
 */
export interface UseSummarySpeechResult {
  isLoading: boolean;
  isDownloading: boolean;
  isPlaying: boolean;
  error: string | null;
  /** Play (synthesizing on first call) or pause if already playing. */
  toggle: (text: string, options?: SummarySpeechOptions) => Promise<void>;
  /** Synthesize and trigger a browser download of the MP3. */
  download: (text: string, options?: SummarySpeechOptions) => Promise<void>;
  stop: () => void;
}

async function fetchTtsMp3(
  text: string,
  options: SummarySpeechOptions & { download?: boolean }
): Promise<Blob> {
  const response = await fetch("/api/ai/tts", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      text,
      filename: options.filename,
      download: options.download ?? false,
    }),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(typeof body.error === "string" ? body.error : "Failed to generate audio");
  }
  return response.blob();
}

function triggerBlobDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

function dispositionFilename(response: Response, fallback: string): string {
  const header = response.headers.get("Content-Disposition");
  const match = header?.match(/filename="([^"]+)"/i);
  return match?.[1] ?? fallback;
}

export function useSummarySpeech(): UseSummarySpeechResult {
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const objectUrlRef = useRef<string | null>(null);
  // Text the cached audio was synthesized from — re-synthesize when it changes.
  const cachedTextRef = useRef<string | null>(null);

  const cleanup = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = "";
      audioRef.current = null;
    }
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    cachedTextRef.current = null;
  }, []);

  useEffect(() => cleanup, [cleanup]);

  const stop = useCallback(() => {
    if (audioRef.current) audioRef.current.pause();
    setIsPlaying(false);
  }, []);

  const download = useCallback(async (text: string, options: SummarySpeechOptions = {}) => {
    setError(null);
    setIsDownloading(true);
    try {
      const filename = mp3FilenameFromTitle(options.filename ?? "ai-summary");
      const response = await fetch("/api/ai/tts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          text,
          filename: options.filename,
          download: true,
        }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(typeof body.error === "string" ? body.error : "Failed to download audio");
      }
      const blob = await response.blob();
      triggerBlobDownload(blob, dispositionFilename(response, filename));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to download audio");
    } finally {
      setIsDownloading(false);
    }
  }, []);

  const toggle = useCallback(
    async (text: string, options: SummarySpeechOptions = {}) => {
      setError(null);

      // Already playing this text → pause.
      if (isPlaying && audioRef.current && cachedTextRef.current === text) {
        audioRef.current.pause();
        setIsPlaying(false);
        return;
      }

      // Cached audio for the same text → resume play.
      if (audioRef.current && cachedTextRef.current === text) {
        try {
          await audioRef.current.play();
          setIsPlaying(true);
        } catch {
          setIsPlaying(false);
        }
        return;
      }

      // Text changed (or first play) → fetch fresh audio.
      cleanup();
      setIsLoading(true);
      try {
        const blob = await fetchTtsMp3(text, options);
        const url = URL.createObjectURL(blob);
        objectUrlRef.current = url;
        cachedTextRef.current = text;

        const audio = new Audio(url);
        audio.addEventListener("ended", () => setIsPlaying(false));
        audio.addEventListener("pause", () => setIsPlaying(false));
        audio.addEventListener("play", () => setIsPlaying(true));
        audioRef.current = audio;

        await audio.play();
        setIsPlaying(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to play audio");
        setIsPlaying(false);
      } finally {
        setIsLoading(false);
      }
    },
    [cleanup, isPlaying]
  );

  return { isLoading, isDownloading, isPlaying, error, toggle, download, stop };
}
